// Polymorphic comment thread. Lists comments on (itemKind, itemId)
// chronologically + an at-bottom composer that supports @mentions
// via type-ahead search of planner users. Mirrors the Drillcut
// CommentThread component.

import { useEffect, useMemo, useRef, useState } from "react";
import { Send } from "lucide-react";
import { tpApi } from "../api";
import { useResource } from "../hooks";
import { Avatar } from "./Avatar";
import { formatDateShort } from "../utils";
import { DC } from "../sheets/createSlideOver/primitives";
import type { Comment, PlannerOwner } from "../../../../../../shared/team-planner/schemas";

interface CommentThreadProps {
  itemKind: "rock" | "milestone" | "todo" | "subtask" | "issue";
  itemId: string;
}

export function CommentThread({ itemKind, itemId }: CommentThreadProps) {
  const comments = useResource(() => tpApi.listComments(itemKind, itemId), [itemKind, itemId]);
  const [body, setBody] = useState("");
  const [mentions, setMentions] = useState<PlannerOwner[]>([]);
  const [posting, setPosting] = useState(false);
  // Mention typeahead — when the user types "@" followed by query
  // letters, we surface a small popover with matching users.
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  type MentionCandidate = PlannerOwner & { pendingProjection: boolean };
  const [mentionCandidates, setMentionCandidates] = useState<MentionCandidate[]>([]);
  const [projecting, setProjecting] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // Live search for mention candidates.
  useEffect(() => {
    if (mentionQuery === null) {
      setMentionCandidates([]);
      return;
    }
    let alive = true;
    void tpApi.searchUsers(mentionQuery).then((rows) => {
      if (alive) setMentionCandidates(rows.slice(0, 6));
    });
    return () => {
      alive = false;
    };
  }, [mentionQuery]);

  function onBodyChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const val = e.target.value;
    setBody(val);
    // Detect the trailing @-mention token.
    const m = val.slice(0, e.target.selectionStart).match(/(?:^|\s)@([\w.\-]*)$/);
    setMentionQuery(m ? (m[1] ?? "") : null);
  }

  async function applyMention(u: MentionCandidate) {
    // Graph-only hit — project into planner_users first so the id we
    // post to /comments resolves on the server. Without this, the
    // notify fan-out treats the Entra Object ID as a UUID, can't find
    // a planner_users row, and silently drops the mention. Mirrors
    // the PersonSelect projection guard in createSlideOver/selects.tsx.
    let resolved: PlannerOwner = u;
    if (u.pendingProjection && u.entraObjectId) {
      setProjecting(true);
      try {
        resolved = await tpApi.projectUser({
          entraObjectId: u.entraObjectId,
          displayName: u.displayName,
          email: u.email,
        });
      } finally {
        setProjecting(false);
      }
    }
    // Re-read the textarea via a functional state update so any
    // keystrokes that landed during the projection await are
    // preserved (Codex PR #60 P2). Reading `body` from closure here
    // would clobber newer edits with stale text.
    const mentionToken = `@${(resolved.displayName ?? "user").replace(/\s+/g, "_")} `;
    setBody((current) => {
      const cursorAt = inputRef.current?.selectionStart ?? current.length;
      const before = current.slice(0, cursorAt);
      const after = current.slice(cursorAt);
      const replaced = before.replace(/(?:^|\s)@([\w.\-]*)$/, (m) => {
        const lead = m.startsWith(" ") ? " " : "";
        return `${lead}${mentionToken}`;
      });
      return replaced + after;
    });
    setMentions((m) =>
      m.find((x) => x.id === resolved.id) ? m : [...m, resolved],
    );
    setMentionQuery(null);
    inputRef.current?.focus();
  }

  async function post() {
    if (!body.trim() || posting) return;
    setPosting(true);
    try {
      await tpApi.postComment(itemKind, itemId, body.trim(), mentions.map((m) => m.id));
      setBody("");
      setMentions([]);
      comments.refresh();
    } finally {
      setPosting(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div
        style={{
          font: "700 10px/1 var(--bsp-font-sans)",
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: DC.grey,
        }}
      >
        Activity · {comments.data?.length ?? 0}{" "}
        {(comments.data?.length ?? 0) === 1 ? "comment" : "comments"}
      </div>

      {comments.loading ? (
        <p style={{ color: DC.grey, fontSize: 13 }}>Loading…</p>
      ) : (comments.data ?? []).length === 0 ? (
        <p style={{ color: DC.grey, fontSize: 13 }}>No comments yet. Start the conversation.</p>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          {(comments.data ?? []).map((c) => (
            <CommentRow key={c.id} comment={c} />
          ))}
        </ul>
      )}

      <div
        style={{
          position: "relative",
          border: `1px solid ${DC.borderStrong}`,
          borderRadius: 8,
          background: DC.surface,
          padding: 10,
        }}
      >
        <textarea
          ref={inputRef}
          value={body}
          onChange={onBodyChange}
          placeholder="Write a comment… use @ to mention someone"
          rows={3}
          style={{
            width: "100%",
            border: 0,
            outline: 0,
            resize: "vertical",
            font: "400 13px/1.45 var(--bsp-font-sans)",
            color: "var(--bsp-fg)",
            background: "transparent",
            minHeight: 56,
          }}
        />
        {mentionQuery !== null && mentionCandidates.length > 0 ? (
          <div
            style={{
              position: "absolute",
              bottom: "calc(100% + 4px)",
              left: 8,
              right: 8,
              maxHeight: 220,
              overflowY: "auto",
              background: DC.surface,
              border: `1px solid ${DC.borderStrong}`,
              borderRadius: 6,
              boxShadow: "var(--bsp-shadow-pop)",
              zIndex: 5,
            }}
          >
            {mentionCandidates.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => void applyMention(u)}
                disabled={projecting}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  padding: "8px 10px",
                  border: 0,
                  background: "transparent",
                  textAlign: "left",
                  cursor: projecting ? "wait" : "pointer",
                  opacity: projecting ? 0.6 : 1,
                }}
              >
                <Avatar owner={u} />
                <span style={{ flex: 1, fontSize: 13 }}>{u.displayName}</span>
                <span style={{ fontSize: 11, color: DC.grey }}>
                  {u.pendingProjection ? `${u.email ?? ""} · from directory` : u.email}
                </span>
              </button>
            ))}
          </div>
        ) : null}
        <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "space-between", marginTop: 8 }}>
          <div style={{ fontSize: 11, color: DC.grey }}>
            {mentions.length > 0
              ? `${mentions.length} mention${mentions.length === 1 ? "" : "s"}`
              : "Markdown not supported — plain text only"}
          </div>
          <button
            type="button"
            className="bsp-btn bsp-btn-primary bsp-btn-sm"
            disabled={posting || !body.trim()}
            onClick={post}
          >
            <Send size={12} /> Post
          </button>
        </div>
      </div>
    </div>
  );
}

function CommentRow({ comment }: { comment: Comment }) {
  // Replace @user_name tokens in the body with bold spans by walking
  // each mention display name.
  const rendered = useMemo(() => {
    let html = escapeHtml(comment.body);
    for (const dn of comment.mentionDisplayNames) {
      const token = `@${(dn || "").replace(/\s+/g, "_")}`;
      html = html.split(token).join(`<strong style="color:var(--bsp-blue)">${token}</strong>`);
    }
    return html;
  }, [comment.body, comment.mentionDisplayNames]);

  return (
    <li
      style={{
        display: "flex",
        gap: 10,
        padding: "10px 12px",
        border: `1px solid ${DC.border}`,
        borderRadius: 6,
        background: DC.surface,
      }}
    >
      <Avatar owner={{ displayName: comment.authorDisplayName, email: null }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <strong style={{ fontSize: 12 }}>{comment.authorDisplayName ?? "Unknown"}</strong>
          <span style={{ fontSize: 11, color: DC.grey }}>{formatDateShort(comment.createdAt)}</span>
        </div>
        <div
          style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap" }}
          dangerouslySetInnerHTML={{ __html: rendered }}
        />
      </div>
    </li>
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
